import { CreateAlternative } from "@/components/pageParts/project/CreateAlternative";
import { useCreateAlternative } from "@/hooks/alternative/useCreateAlternative";
import { RootState } from "@/store/store";
import { setToast } from "@/store/systemSlice";
import { CreateAlternativeRequest } from "@/types/alternative";
import { FilesTypes, UploadFilesTableData } from "@/types/file";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import styles from "./css/CreateUpdateAlternativePage.module.css";
import { BackButton } from "@/components/Buttons/BackButton";
import { currentDate } from "@/utils/date";
import { Button } from "@/components/Buttons";
import { useExtractModelData } from "@/hooks/alternative/useExtractModelData";
import { getEntityIdPair } from "@/utils/getEntityPairId";

export const CreateAlternativePage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {t} = useTranslation("CreateProjectPage");
    const {entityName, cityId, projectId} = useParams<{entityName: string, cityId: string, projectId: string}>();
    const city = useSelector((state: RootState) => state.entity.cities.find((city) => city.id === cityId));
    const [stepsCompleted, setStepsCompleted] = useState<boolean[]>([true, false, true, true, true]);
    const [modelDataLoading, setModelDataLoading] = useState<boolean>(true);
    
    const {userAo, userCity, entityType} = useSelector((state: RootState) => state.entity);
    const [alternativeFieldsCompleted, setAlternativeFieldsCompleted] = useState<boolean>(false);
    const [createLoading, setCreateLoading] = useState<boolean>(false);

    const [alternativeFiles, setAlternativeFiles] = useState<UploadFilesTableData[]>([
        {
            type: "3dmodel",
            file: null,
            file_name: "",
            created_at: "",
            accept_types: [".3dm", ".glb", ".gltf"]
        }
        
    ]);

    const [createAlternativeData, setCreateAlternativeData] = useState<CreateAlternativeRequest>({
        name: "",
        description: "",
        ao_id: userAo?.id as string,
        city_id: cityId as string,
        neighborhood_id: "",
        project_id: projectId as string,
        residential_area_planned: undefined,
        residental_unit_area_planned: undefined,
        commercial_area_planned: undefined,
        public_building_area_planned: undefined,
        industrial_area_planned: undefined,
        future_residential_parking: undefined,
        future_commercial_parking: undefined,
        future_industrial_parking: undefined,
        future_public_parking: undefined,

        planned_additional_production: undefined,
        building_energy_rating: "B",
        roofs_area: undefined,
        facades_area_north: undefined,
        facades_area_south: undefined,
        facades_area_east: undefined,
        facades_area_west: undefined,
        facades_area_total: undefined,
        residents_final_target_year: undefined
    });
    
    const createAlternative = useCreateAlternative({
        onSuccess(data) {
            setCreateLoading(false);
            dispatch(
                setToast({
                  toastTitle: data.message,
                  toastMessage: "",
                  toastType: "success",
                })
              );
            navigate(`/${entityName}/project/${cityId}/${projectId}`);
        },
        onError(error) {
            setCreateLoading(false);
            dispatch(
                setToast({
                  toastTitle: error.response.data.message,
                  toastMessage: "",
                  toastType: "error",
                })
            );
        },
    })

    useEffect(() => {
        if (!city && !userCity) {
            navigate("/404");
        }
    }, [city]);

    const extractModelData = useExtractModelData({
        onSuccess(data) {
            setCreateAlternativeData({
                ...createAlternativeData,
                roofs_area: data.total_roof_area,
                facades_area_north: data.total_facades.north,
                facades_area_south: data.total_facades.south,
                facades_area_east: data.total_facades.east,
                facades_area_west: data.total_facades.west,
                facades_area_total: data.total_facades.total
            });
            setModelDataLoading(false);
        },
        onError(error) {
            console.error("Error in extract Model Data");
            setModelDataLoading(false);
        }
    });

    const handleCreateAlternativeData = (data: CreateAlternativeRequest) => {
        const newData = {
            ...data, 
            facades_area_total: (data.facades_area_north || 0) + 
                              (data.facades_area_south || 0) + 
                              (data.facades_area_east || 0) + 
                              (data.facades_area_west || 0)
        };
        setCreateAlternativeData(newData);
    }

    const handleUploadFile = (type: FilesTypes, file: File, project: boolean) => {
        const newAlternativeFiles = [...alternativeFiles];
        const index = newAlternativeFiles.findIndex(file => file.type === type);
        newAlternativeFiles[index] = {type, file, file_name: file.name, created_at: currentDate(), accept_types: alternativeFiles[index].accept_types};
        setAlternativeFiles(newAlternativeFiles);
        if (type === "3dmodel") {

            const entityPair = getEntityIdPair(entityType, userCity, userAo);
            const requestData = {files: {"3dmodel": file}, ...entityPair};
            extractModelData.mutate(requestData);
        }
    }

    const handleCreateAlternative = () => {
        setCreateLoading(true);
        const filesDict = alternativeFiles
        .filter(file => file.file)
        .reduce((acc, file) => ({
            ...acc,
            [file.type]: file.file
        }), {} as Record<FilesTypes, File>);
        createAlternative.mutate({...createAlternativeData, files: filesDict});
    }

    return (
        <div className={styles.container}>
        <div className={styles.headerContainer}>
            <h1 className={styles.title}>{t("createProject3")}</h1>
            <div className={styles.backButtonContainer}>
                <BackButton/>
            </div>
        </div>
        <div className={styles.formContainer}>
            {<CreateAlternative  disabled={modelDataLoading} onChange={handleCreateAlternativeData} data={createAlternativeData} setStepsCompleted={setStepsCompleted} alternativeFiles={alternativeFiles} onUploadFile={(type: FilesTypes, file: File) => handleUploadFile(type, file, false)} fieldsCompleted={alternativeFieldsCompleted} setFieldsCompleted={setAlternativeFieldsCompleted}/>}
            <div className={styles.stepsContainerOnePage}>
                    <div className={styles.stepsSubContainer}>
                        <Button icon="no" text={`צור חלופה`} onClick={handleCreateAlternative} id="continue-btn" className={styles.continueBtn} disabled={!stepsCompleted[1] || 
                                                                         createLoading || 
                                                                         (alternativeFiles.find(file => file.type === "3dmodel")?.file !== null && modelDataLoading)} loading={createLoading}/>
                    </div>
            </div>
        </div>
    </div>
    )
}